import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { formatKickoff } from '@/utils/formatKickoff';
import { formatPhone, buildSmsUri } from '@/utils/formatPhone';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

// Mirrors the worker's plausibility bounds in src/lib/season.ts
const MIN_SEASON_YEAR = 1900;
const MAX_SEASON_YEAR = 2999;

const toSeasonYear = (raw) => {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!/^\d+$/.test(s)) return null;
  const n = parseInt(s, 10);
  return n >= MIN_SEASON_YEAR && n <= MAX_SEASON_YEAR ? n : null;
};

const buildEspnScoreboardUrlsForBrowser = (seasonYear, weekNumber) => {
  const cdnParams = new URLSearchParams({ xhr: '1', limit: '50', seasontype: '2' });
  if (Number.isInteger(weekNumber)) cdnParams.set('week', String(weekNumber));
  if (Number.isInteger(seasonYear)) cdnParams.set('year', String(seasonYear));

  const siteParams = new URLSearchParams({ seasontype: '2' });
  if (Number.isInteger(weekNumber)) siteParams.set('week', String(weekNumber));
  if (Number.isInteger(seasonYear)) siteParams.set('dates', String(seasonYear));

  return [
    `https://cdn.espn.com/core/nfl/scoreboard?${cdnParams.toString()}`,
    `https://site.api.espn.com/apis/site/v2/sports/football/nfl/scoreboard?${siteParams.toString()}`,
    `https://site.web.api.espn.com/apis/site/v2/sports/football/nfl/scoreboard?${siteParams.toString()}`,
  ];
};

const fetchScoreboardInBrowser = async (candidateUrls) => {
  let lastErr = 'Browser ESPN fetch failed';
  for (const url of candidateUrls) {
    if (!url || typeof url !== 'string') continue;
    try {
      const espnRes = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!espnRes || !espnRes.ok) {
        lastErr = `Browser ESPN fetch failed: ${espnRes ? `${espnRes.status || ''} ${espnRes.statusText || ''}`.trim() : 'no response'}`;
        continue;
      }
      const json = await espnRes.json();
      if (json && typeof json === 'object' && !Array.isArray(json)) {
        return { scoreboard: json, sourceUrl: url };
      }
      lastErr = 'Browser ESPN fetch returned invalid payload';
    } catch (err) {
      lastErr = `Browser ESPN fetch failed: ${(err && err.message) || 'network error'}`;
    }
  }
  throw new Error(lastErr);
};

const formatSyncErrorNotice = (data, fallbackSuffix = '') => {
  const baseMsg =
    data && (data.message || data.error)
      ? `${data.error || 'Failed to sync from ESPN'}${data.message ? `: ${data.message}` : ''}`
      : 'Failed to sync from ESPN';
  const msg = `${baseMsg}${fallbackSuffix || ''}`;
  const extra =
    data && (data.espn_url || data.details)
      ? `\n${data.espn_url ? `URL: ${data.espn_url}` : ''}${data.details ? `\n${String(data.details).slice(0, 160)}` : ''}`
      : '';
  return { msg, noticeText: `${msg}${extra}` };
};

export default function AdminConfig({ token, me }) {
  const [weeksList, setWeeksList] = useState([]);
  const [selectedWeekId, setSelectedWeekId] = useState('');
  const [configBooted, setConfigBooted] = useState(false);
  const [siteNowISO, setSiteNowISO] = useState('');
  const [lockTimeInput, setLockTimeInput] = useState('');
  // ESPN sync inputs (optional; default to selected week values)
  const [seasonYearInput, setSeasonYearInput] = useState('');
  const [weekNumberInput, setWeekNumberInput] = useState('');
  const [cronMode, setCronMode] = useState('always');
  // Resolved active season (GET /api/seasons) vs. the explicit settings.active_season override
  const [resolvedActiveSeason, setResolvedActiveSeason] = useState(null);
  const [activeSeasonOverride, setActiveSeasonOverride] = useState(null);
  const [seasonsList, setSeasonsList] = useState([]);
  const [activeSeasonInput, setActiveSeasonInput] = useState('');
  const [savingActiveSeason, setSavingActiveSeason] = useState(false);
  const [cronSelectEpoch, setCronSelectEpoch] = useState(0);
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');
  const [weekPicks, setWeekPicks] = useState([]);
  const [weekGames, setWeekGames] = useState([]);
  const [teams, setTeams] = useState([]);
  const [override, setOverride] = useState({ entry_id: '', team_abbr: '' });
  const [loadingEligible, setLoadingEligible] = useState(false);
  // Ensure week dropdown is sorted by created_at (oldest -> newest). If created_at missing, treat as 0.
  const weeksListSortedByCreatedAt = useMemo(() => {
    const arr = (weeksList || []).slice();
    arr.sort((a, b) => {
      const at = a && a.created_at ? new Date(a.created_at).getTime() : 0;
      const bt = b && b.created_at ? new Date(b.created_at).getTime() : 0;
      return at - bt; // ascending by created_at
    });
    return arr;
  }, [weeksList]);
  const [pickSort, setPickSort] = useState('default'); // default | noPickFirst | byEntry | byUser | byEmail
  const [pickFilter, setPickFilter] = useState('all'); // all | noPick | hasPick
  const [pickSearch, setPickSearch] = useState(''); // search filter for picks
  // Small admin banner for ESPN sync results
  const [syncNotice, setSyncNotice] = useState({ type: '', text: '' });
  // Add Week inputs
  const [newSeasonYear, setNewSeasonYear] = useState('');
  const [newWeekNumber, setNewWeekNumber] = useState('');
  // Randomization flags
  const [overwritePicks, setOverwritePicks] = useState(false);
  // Collapsible section states (all default open in-memory)
  const [activeSeasonOpen, setActiveSeasonOpen] = useState(true);
  const [weeksMgmtOpen, setWeeksMgmtOpen] = useState(true);
  const [picksMgmtOpen, setPicksMgmtOpen] = useState(true);

  const activeEntriesCount = useMemo(() => {
    try {
      const ids = new Set((weekPicks || []).map(p => p && p.entry_id).filter(Boolean));
      return ids.size;
    } catch { return 0; }
  }, [weekPicks]);

  const toLocalLockInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const selectedWeekIsLocked = useMemo(() => {
    const w = weeksList.find((x) => x.id === selectedWeekId);
    if (!w || !w.lock_time) return false;
    const nowMs = siteNowISO ? new Date(siteNowISO).getTime() : Date.now();
    return nowMs >= new Date(w.lock_time).getTime();
  }, [weeksList, selectedWeekId, siteNowISO]);

  const sortedWeekPicks = useMemo(() => {
    let arr = (weekPicks || []).slice();

    // Apply pick submission filter
    if (pickFilter === 'noPick') {
      arr = arr.filter(p => !!(p.no_pick || !p.team_abbr));
    } else if (pickFilter === 'hasPick') {
      arr = arr.filter(p => !p.no_pick && !!p.team_abbr);
    }
    
    // Apply search filter
    if (pickSearch && pickSearch.trim()) {
      const searchTerm = pickSearch.toLowerCase().trim();
      const searchDigits = searchTerm.replace(/\D/g, '');
      arr = arr.filter(p => {
        const nameMatch = (p.user_name || '').toLowerCase().includes(searchTerm);
        const emailMatch = (p.user_email || '').toLowerCase().includes(searchTerm);
        const entryMatch = (p.entry_name || '').toLowerCase().includes(searchTerm);
        const mobileRaw = (p.user_mobile || '').toLowerCase();
        const mobileFormatted = formatPhone(p.user_mobile).toLowerCase();
        const mobileMatch = mobileRaw.includes(searchTerm) ||
               mobileFormatted.includes(searchTerm) ||
               (searchDigits.length >= 3 && mobileRaw.replace(/\D/g, '').includes(searchDigits));
        return nameMatch || emailMatch || entryMatch || mobileMatch;
      });
    }
    
    // Then apply sorting
    if (pickSort === 'noPickFirst') {
      arr.sort((a, b) => {
        const aNo = !!(a.no_pick || !a.team_abbr);
        const bNo = !!(b.no_pick || !b.team_abbr);
        if (aNo !== bNo) return aNo ? -1 : 1;
        return (a.entry_name || '').localeCompare(b.entry_name || '') || (a.user_name || '').localeCompare(b.user_name || '');
      });
    } else if (pickSort === 'byEntry') {
      arr.sort((a, b) => (a.entry_name || '').localeCompare(b.entry_name || '') || (a.user_name || '').localeCompare(b.user_name || ''));
    } else if (pickSort === 'byUser') {
      arr.sort((a, b) => (a.user_name || '').localeCompare(b.user_name || '') || (a.entry_name || '').localeCompare(b.entry_name || ''));
    } else if (pickSort === 'byEmail') {
      arr.sort((a, b) => (a.user_email || '').localeCompare(b.user_email || '') || (a.entry_name || '').localeCompare(b.entry_name || ''));
    }
    return arr;
  }, [weekPicks, pickFilter, pickSort, pickSearch]);

  const selectedWeek = useMemo(
    () => weeksList.find((w) => w.id === selectedWeekId) || null,
    [weeksList, selectedWeekId],
  );

  const byeTeamsList = useMemo(() => {
    const arr = Array.isArray(selectedWeek?.bye_teams) ? selectedWeek.bye_teams : [];
    return arr.map(String).filter(Boolean);
  }, [selectedWeek]);

  useEffect(() => {
    if (!token || !me || !me.is_admin) return;
  (async () => {
      try {
    const [weeksRes, siteRes, teamsRes, settingsRes, seasonsRes] = await Promise.all([
          fetch(`${API_BASE}/api/weeks`),
          fetch(`${API_BASE}/api/site-time`),
          fetch(`${API_BASE}/api/teams`),
          fetch(`${API_BASE}/api/admin/settings`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE}/api/seasons`),
        ]);
        const weeks = (await weeksRes.json().catch(() => [])) || [];
        setWeeksList(Array.isArray(weeks) ? weeks : []);
        const site = await siteRes.json().catch(() => ({}));
        if (teamsRes && teamsRes.ok) {
          const tData = await teamsRes.json().catch(() => []);
          setTeams(Array.isArray(tData) ? tData : []);
        }
        if (weeksRes.ok && Array.isArray(weeks) && weeks.length) {
          const cur = weeks.find(w => w.is_current) || weeks[0];
          setSelectedWeekId(cur.id);
          if (cur.lock_time) setLockTimeInput(toLocalLockInput(cur.lock_time));
        }
        if (siteRes.ok && site.now_iso) {
          setSiteNowISO(site.now_iso);
        }
        if (settingsRes && settingsRes.ok) {
          const sData = await settingsRes.json().catch(() => ({}));
          if (sData && (sData.cron_mode === 'off' || sData.cron_mode === 'always' || sData.cron_mode === 'windowed')) {
            setCronMode(sData.cron_mode);
          }
          const override = toSeasonYear(sData && sData.active_season);
          setActiveSeasonOverride(override);
          if (override != null) setActiveSeasonInput(String(override));
        }
        if (seasonsRes && seasonsRes.ok) {
          const seasonsData = (await seasonsRes.json().catch(() => ({}))) || {};
          setResolvedActiveSeason(toSeasonYear(seasonsData.active_season));
          setSeasonsList(Array.isArray(seasonsData.seasons) ? seasonsData.seasons : []);
        }
  } catch {}
  finally { setConfigBooted(true); }
    })();
  }, [token, me]);

  // Load picks when week changes
  useEffect(() => {
    if (!selectedWeekId) { setWeekPicks([]); return; }
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/weeks/${encodeURIComponent(selectedWeekId)}/picks`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json().catch(() => []);
        setWeekPicks(Array.isArray(data) ? data : []);
      } catch {}
    })();
  }, [selectedWeekId, token]);

  // Load games when week changes
  useEffect(() => {
    if (!selectedWeekId) { setWeekGames([]); return; }
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/weeks/${encodeURIComponent(selectedWeekId)}/games`);
        const data = await res.json().catch(() => []);
        setWeekGames(Array.isArray(data) ? data : []);
      } catch {}
    })();
  }, [selectedWeekId]);

  // Sync lock time / ESPN inputs when selected week changes
  useEffect(() => {
    const toLocalInput = (iso) => {
      if (!iso) return '';
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    const w = weeksList.find(w => w.id === selectedWeekId);
    setLockTimeInput(w && w.lock_time ? toLocalInput(w.lock_time) : '');
    // Default ESPN sync inputs from selected week
    if (w) {
      setSeasonYearInput(String(w.season_year || ''));
      setWeekNumberInput(String(w.week_number || ''));
    } else {
      setSeasonYearInput('');
      setWeekNumberInput('');
    }
  }, [selectedWeekId, weeksList]);

  if (!token) return <Navigate to="/auth" replace />;
  if (me == null) return <div className="mx-auto max-w-7xl px-4 py-6"><div>Loading…</div></div>;
  if (me && !me.is_admin) return <div className="mx-auto max-w-7xl px-4 py-6"><h2>Access denied</h2><Link to="/">Return to Dashboard</Link></div>;

  const adminSetCronMode = async (mode) => {
    setError(''); setToast('');
    if (mode === 'off') {
      if (!window.confirm('Turn off the ESPN sync cron for this environment? Scores will not update automatically until you re-enable it.')) {
        setCronSelectEpoch((n) => n + 1); // remount select so controlled value wins after cancel
        return;
      }
    }
    try {
      const res = await fetch(`${API_BASE}/api/admin/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ cron_mode: mode }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to update cron mode');
      const next = data.cron_mode === 'off' || data.cron_mode === 'always' || data.cron_mode === 'windowed'
        ? data.cron_mode
        : mode;
      setCronMode(next);
      setToast(`Cron mode set to ${next}`);
    } catch (e) { setError(e.message); }
  };
  const refreshSeasons = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/seasons`);
      const data = (await res.json().catch(() => ({}))) || {};
      if (!res.ok) return;
      setResolvedActiveSeason(toSeasonYear(data.active_season));
      setSeasonsList(Array.isArray(data.seasons) ? data.seasons : []);
    } catch { /* non-fatal */ }
  };

  const patchActiveSeason = async (value) => {
    setError(''); setToast('');
    try {
      setSavingActiveSeason(true);
      const res = await fetch(`${API_BASE}/api/admin/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ active_season: value }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to update active season');
      const saved = toSeasonYear(data.active_season);
      setActiveSeasonOverride(saved);
      setActiveSeasonInput(saved == null ? '' : String(saved));
      await refreshSeasons();
      setToast(saved == null ? 'Active season override cleared' : `Active season set to ${saved}`);
    } catch (e) { setError(e.message); }
    finally { setSavingActiveSeason(false); }
  };

  const adminSaveActiveSeason = async () => {
    const year = toSeasonYear(activeSeasonInput);
    if (year == null) {
      setToast('');
      setError(`Active season must be a whole year between ${MIN_SEASON_YEAR} and ${MAX_SEASON_YEAR}`);
      return;
    }
    await patchActiveSeason(year);
  };

  const adminClearActiveSeason = async () => {
    if (!window.confirm('Clear the active season override? The app will fall back to the current week\'s season.')) return;
    await patchActiveSeason(null);
  };

  const adminSetCurrentWeek = async () => {
    setError(''); setToast('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/set-current-week`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ week_id: selectedWeekId })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to set current week');
      setToast('Current week updated');
      setWeeksList(prev => prev.map(w => ({ ...w, is_current: w.id === selectedWeekId })));
    } catch (e) { setError(e.message); }
  };
  const adminUpdateLockTime = async () => {
    setError(''); setToast('');
    try {
      const res = await fetch(`${API_BASE}/api/weeks/${encodeURIComponent(selectedWeekId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ lock_time: new Date(lockTimeInput).toISOString() })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to update lock time');
      setToast('Lock time updated');
      setWeeksList(prev => prev.map(w => w.id === selectedWeekId ? { ...w, lock_time: data.lock_time } : w));
      // Reflect updated lock time in the input (convert ISO to local display)
      try {
        const d = new Date(data.lock_time);
        const pad = (n) => String(n).padStart(2, '0');
        setLockTimeInput(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
      } catch {}
    } catch (e) { setError(e.message); }
  };
  const adminResetLockAuto = async () => {
    setError(''); setToast('');
    try {
      const res = await fetch(`${API_BASE}/api/weeks/${encodeURIComponent(selectedWeekId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ lock_time: 'auto' })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to reset lock time');
      setToast('Lock time reset to Sunday noon');
      setWeeksList(prev => prev.map(w => w.id === selectedWeekId ? { ...w, lock_time: data.lock_time } : w));
      // Update the input to show local time (should display 12:00 when browser is in ET)
      try {
        const d = new Date(data.lock_time);
        const pad = (n) => String(n).padStart(2, '0');
        setLockTimeInput(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
      } catch {}
    } catch (e) { setError(e.message); }
  };

  const adminCreateWeek = async () => {
    try {
      setError(''); setToast(''); setSyncNotice({ type: '', text: '' });
      const sy = parseInt(newSeasonYear, 10);
      const wn = parseInt(newWeekNumber, 10);
      if (!Number.isFinite(sy) || !Number.isFinite(wn)) throw new Error('Enter a valid season year and week number');
      const res = await fetch(`${API_BASE}/api/weeks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ season_year: sy, week_number: wn })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to create week');
      // Refresh list and select new week
      try {
        const wRes = await fetch(`${API_BASE}/api/weeks`);
        const wData = await wRes.json().catch(() => []);
        if (Array.isArray(wData)) setWeeksList(wData);
      } catch {}
      if (data && data.id) setSelectedWeekId(data.id);
      setToast(`Week ${wn} (${sy}) created`);
  setNewSeasonYear('');
  setNewWeekNumber('');
    } catch (e) { setError(e.message); }
  };

  const adminRandomizePicks = async () => {
    if (!selectedWeekId) return;
  if (!window.confirm('Randomize picks for all non-eliminated entries for this week? This may overwrite existing picks if selected. Proceed?')) return;
    try {
      setError(''); setToast('');
      const res = await fetch(`${API_BASE}/api/admin/weeks/${encodeURIComponent(selectedWeekId)}/randomize-picks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ overwrite: !!overwritePicks })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to randomize picks');
      setToast(`Randomized picks (created: ${data?.counts?.created || 0}, updated: ${data?.counts?.updated || 0}, skipped: ${data?.counts?.skipped || 0})`);
      // Refresh picks
      try {
        const res2 = await fetch(`${API_BASE}/api/weeks/${encodeURIComponent(selectedWeekId)}/picks`, { headers: { Authorization: `Bearer ${token}` } });
        const data2 = await res2.json().catch(() => []);
        setWeekPicks(Array.isArray(data2) ? data2 : []);
      } catch {}
    } catch (e) { setError(e.message); }
  };

  const adminRandomizeScores = async () => {
    if (!selectedWeekId) return;
    if (!window.confirm('Randomize game scores for this week and set each game to In Progress? This may be overwritten by live sync later. Proceed?')) return;
    try {
      setError(''); setToast('');
      const res = await fetch(`${API_BASE}/api/admin/weeks/${encodeURIComponent(selectedWeekId)}/randomize-scores`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({})
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to randomize scores');
      setToast(`Randomized scores for ${data?.updated || 0} games (in progress)`);
      try {
        const [gRes, pRes] = await Promise.all([
          fetch(`${API_BASE}/api/weeks/${encodeURIComponent(selectedWeekId)}/games`),
          fetch(`${API_BASE}/api/weeks/${encodeURIComponent(selectedWeekId)}/picks`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        const [gData, pData] = await Promise.all([gRes.json().catch(() => []), pRes.json().catch(() => [])]);
        setWeekGames(Array.isArray(gData) ? gData : []);
        setWeekPicks(Array.isArray(pData) ? pData : []);
      } catch {}
    } catch (e) { setError(e.message); }
  };

  const adminFinalizeScores = async () => {
    if (!selectedWeekId) return;
    if (!window.confirm('Set all games for this week to Final? This is for testing only and may be overwritten by live sync later. Proceed?')) return;
    try {
      setError(''); setToast('');
      const res = await fetch(`${API_BASE}/api/admin/weeks/${encodeURIComponent(selectedWeekId)}/finalize-scores`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to finalize scores');
      const elimCount = Array.isArray(data?.eliminated_entries) ? data.eliminated_entries.length : (data?.eliminated_count || 0);
      setToast(`Marked ${data?.updated || 0} games as final; eliminated entries: ${elimCount}`);
      // Refresh games and picks
      try {
        const [gRes, pRes] = await Promise.all([
          fetch(`${API_BASE}/api/weeks/${encodeURIComponent(selectedWeekId)}/games`),
          fetch(`${API_BASE}/api/weeks/${encodeURIComponent(selectedWeekId)}/picks`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        const [gData, pData] = await Promise.all([gRes.json().catch(() => []), pRes.json().catch(() => [])]);
        setWeekGames(Array.isArray(gData) ? gData : []);
        setWeekPicks(Array.isArray(pData) ? pData : []);
      } catch {}
    } catch (e) { setError(e.message); }
  };

  const refreshWeeksList = async () => {
    try {
      const weeksRes = await fetch(`${API_BASE}/api/weeks`);
      const weeks = (await weeksRes.json().catch(() => [])) || [];
      if (weeksRes.ok && Array.isArray(weeks)) setWeeksList(weeks);
    } catch { /* non-fatal */ }
  };

  // Toggle is_locked / is_ineligible on a game row
  const adminToggleGameFlag = async (game, patch) => {
    if (!selectedWeekId || !game?.id) return;
    try {
      setError(''); setToast('');
      const res = await fetch(`${API_BASE}/api/games/${encodeURIComponent(game.id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(patch),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to update game');
      setWeekGames((prev) => prev.map((g) => (g.id === game.id ? { ...g, ...data } : g)));
      if (patch.is_ineligible !== undefined) {
        await refreshWeeksList();
        setToast(patch.is_ineligible ? 'Game marked ineligible' : 'Game marked eligible');
      } else if (patch.is_locked !== undefined) {
        setToast(patch.is_locked ? 'Game locked' : 'Game unlocked');
      }
    } catch (e) { setError(e.message); }
  };

  // Load placeholder rows for all alive entries without a pick for selected week
  const adminLoadEligibleNoPickRows = async () => {
    if (!selectedWeekId) return;
    try {
      setError(''); setToast(''); setLoadingEligible(true);
      // Fetch current week's picks and all entries (admin view)
  const [picksRes, entriesRes] = await Promise.all([
        fetch(`${API_BASE}/api/weeks/${selectedWeekId}/picks`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE}/api/admin/entries`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      const [picksJson, entriesJson] = await Promise.all([
        picksRes.json().catch(() => []),
        entriesRes.json().catch(() => [])
      ]);
      if (!picksRes.ok) throw new Error((picksJson && picksJson.error) || 'Failed to load picks');
      if (!entriesRes.ok) throw new Error((entriesJson && entriesJson.error) || 'Failed to load entries');
      const picksData = Array.isArray(picksJson) ? picksJson : [];
      let entries = Array.isArray(entriesJson) ? entriesJson : [];

      // Fallback: some environments may return an empty admin entries array; in that case, construct entries
      // by listing all users and fetching their entries (admin is allowed to access /api/users/:id/entries)
      if (entries.length === 0) {
        try {
          const usersRes = await fetch(`${API_BASE}/api/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
          const usersJson = await usersRes.json().catch(() => []);
          if (usersRes.ok && Array.isArray(usersJson) && usersJson.length > 0) {
            const usersById = new Map(usersJson.map(u => [u.id, u]));
            const entryLists = await Promise.all(usersJson.map(async (u) => {
              try {
                const er = await fetch(`${API_BASE}/api/users/${u.id}/entries`, { headers: { Authorization: `Bearer ${token}` } });
                const ej = await er.json().catch(() => []);
                if (!er.ok || !Array.isArray(ej)) return [];
                return ej.map(e => ({
                  id: e.id,
                  user_id: e.user_id,
                  user_email: (usersById.get(e.user_id) || {}).email || null,
                  user_name: `${((usersById.get(e.user_id) || {}).first_name || '').trim()} ${((usersById.get(e.user_id) || {}).last_name || '').trim()}`.trim() || (usersById.get(e.user_id) || {}).email || null,
                  name: e.name,
                  is_eliminated: e.is_eliminated,
                  created_at: e.created_at,
                  updated_at: e.updated_at
                }));
              } catch {
                return [];
              }
            }));
            entries = entryLists.flat();
          }
        } catch {
          // ignore fallback errors; entries will remain empty
        }
      }

  // Consider an entry "picked" if there is a real pick with a team_abbr for this week
  const pickedIdsFromServer = new Set(picksData.filter(p => p && p.entry_id && p.team_abbr).map(p => p.entry_id));

  // Eligible = not eliminated
  const eligible = entries.filter(e => e && e.id && (e.is_eliminated !== true));

  // Current UI table entries (may already include placeholders)
  const currentIds = new Set((weekPicks || []).map(p => p.entry_id));

  // Missing by comparing server picked IDs
  const missingByServer = eligible.filter(e => !pickedIdsFromServer.has(e.id));
  // Missing by comparing what's currently visible in the table
  const missingByUI = eligible.filter(e => !currentIds.has(e.id));
  // Union of both missing sets (robust to server not including placeholders)
  const missingMap = new Map();
  for (const e of missingByServer) missingMap.set(e.id, e);
  for (const e of missingByUI) missingMap.set(e.id, e);
  const missing = Array.from(missingMap.values());

      // Build no-pick placeholders for missing entries
      const placeholders = missing.map(e => ({
        id: `nopick-${selectedWeekId}-${e.id}`,
        entry_id: e.id,
        entry_name: e.name || e.id,
        user_name: e.user_name || '',
        user_email: e.user_email || '',
        user_mobile: e.user_mobile || '',
        week_id: selectedWeekId,
        team_abbr: '',
        no_pick: true
      }));

      if (placeholders.length === 0) {
        setToast(`No eligible entries to add (eligible=${eligible.length}, table=${(weekPicks||[]).length})`);
      } else {
        // Merge with existing UI state, dedupe by entry_id to avoid duplicates
        setWeekPicks(prev => {
          const byEntry = new Map((prev || []).map(p => [p.entry_id, p]));
          for (const np of placeholders) {
            if (!byEntry.has(np.entry_id)) byEntry.set(np.entry_id, np);
          }
          return Array.from(byEntry.values());
        });
        setToast(`Loaded ${placeholders.length} eligible entries without picks`);
      }
    } catch (e) {
      setError(e.message || 'Failed to load eligible entries');
    } finally {
      setLoadingEligible(false);
    }
  };

  const adminSyncEspn = async (mode = 'update_scores') => {
    try {
      setError(''); setToast(''); setSyncNotice({ type: '', text: '' });
      // Prefer explicit inputs; otherwise fall back to selected week's values
      const sel = weeksList.find(w => w.id === selectedWeekId) || {};
      const sy = seasonYearInput ? parseInt(seasonYearInput, 10) : (sel.season_year || undefined);
      const wn = weekNumberInput ? parseInt(weekNumberInput, 10) : (sel.week_number || undefined);

      if (mode === 'full_resync' && weekGames.length > 0) {
        const isCurrent = !!sel.is_current;
        const msg = isCurrent
          ? 'Full Re-sync will REPLACE the current week\'s game slate. Lock/ineligible flags are rematched by HOME|AWAY when they still exist. Click OK to proceed.'
          : 'Full Re-sync will REPLACE this week\'s game slate. Lock/ineligible flags are rematched by HOME|AWAY when they still exist. Click OK to proceed.';
        if (!window.confirm(msg)) return;
      }

      const body = { mode };
      if (Number.isFinite(sy)) body.season_year = sy;
      if (Number.isFinite(wn)) body.week_number = wn;
      let res = await fetch(`${API_BASE}/api/admin/sync-games/espn`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      let data = await res.json().catch(() => ({}));
      let usedBrowserFallback = false;

      const isEspnUpstreamFailure =
        !res.ok && /ESPN fetch failed/i.test(String((data && (data.error || data.message)) || ''));

      if (isEspnUpstreamFailure) {
        const serverUrls = Array.isArray(data && data.espn_urls) ? data.espn_urls.filter(Boolean) : [];
        const defaultUrls = buildEspnScoreboardUrlsForBrowser(sy, wn);
        const candidateUrls = Array.from(new Set([...serverUrls, ...defaultUrls]));
        try {
          const { scoreboard, sourceUrl } = await fetchScoreboardInBrowser(candidateUrls);
          res = await fetch(`${API_BASE}/api/admin/sync-games/espn`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ ...body, scoreboard, source_url: sourceUrl }),
          });
          data = await res.json().catch(() => ({}));
          usedBrowserFallback = true;
        } catch (browserErr) {
          const { msg, noticeText } = formatSyncErrorNotice(
            data,
            ` (browser fallback also failed: ${browserErr.message || 'unable to fetch ESPN scoreboard'})`,
          );
          setSyncNotice({ type: 'error', text: noticeText });
          throw new Error(msg);
        }
      }

      if (!res.ok) {
        const { msg, noticeText } = formatSyncErrorNotice(data);
        setSyncNotice({ type: 'error', text: noticeText });
        throw new Error(msg);
      }
      const c = data.counts || {};
      const cr = c.created || 0;
      const up = c.updated || 0;
      const miss = c.missing || 0;
      const tot = c.total || (cr + up);
      const syText = data.season_year != null ? data.season_year : sy;
      const wnText = data.week_number != null ? data.week_number : wn;
      const modeLabel = mode === 'full_resync' ? 'Full Re-sync' : 'Update Scores';
      const fallbackSuffix = usedBrowserFallback ? ' (via browser fallback)' : '';
      let success;
      if (mode === 'full_resync') {
        success = `ESPN ${modeLabel} complete: ${tot} games (${cr} created) for Season ${syText} Week ${wnText}${fallbackSuffix}`;
      } else {
        success = `ESPN ${modeLabel} complete: ${up} updated` +
          (miss ? `, ${miss} unmatched` : '') +
          ` (${tot} from ESPN) for Season ${syText} Week ${wnText}${fallbackSuffix}`;
      }
      if (data.source_url) success += `\nURL: ${data.source_url}`;
      setSyncNotice({ type: 'success', text: success });
      // Refresh games and weeks (for ineligible updates) after sync
      try {
        const [gRes, wRes] = await Promise.all([
          fetch(`${API_BASE}/api/weeks/${encodeURIComponent(selectedWeekId)}/games`),
          fetch(`${API_BASE}/api/weeks`)
        ]);
        const [gData, wData] = await Promise.all([
          gRes.json().catch(() => []),
          wRes.json().catch(() => [])
        ]);
        if (Array.isArray(gData)) setWeekGames(gData);
        if (Array.isArray(wData)) setWeeksList(wData);
      } catch {}
    } catch (e) {
      setError(e.message);
    }
  };

  const adminDeleteWeek = async () => {
    if (!selectedWeekId) return;
    const w = weeksList.find(w => w.id === selectedWeekId);
    if (w && w.is_current) { setError('Cannot delete the current week'); return; }
    if (!window.confirm('Delete this week and all its picks and games? This cannot be undone.')) return;
    try {
      setError(''); setToast('');
      const res = await fetch(`${API_BASE}/api/weeks/${encodeURIComponent(selectedWeekId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok && res.status !== 204) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to delete week');
      }
      setWeeksList(prev => prev.filter(w => w.id !== selectedWeekId));
      setSelectedWeekId('');
      setWeekPicks([]);
      setToast('Week deleted');
    } catch (e) { setError(e.message); }
  };

  const adminToggleWeekLock = async () => {
    if (!selectedWeekId) return;
    const locking = !selectedWeekIsLocked;
    const confirmMsg = locking
      ? 'Lock this week now? Picks will be closed immediately.'
      : 'Unlock this week? Picks will reopen until the restored/adjusted lock time.';
    if (!window.confirm(confirmMsg)) return;
    try {
      setError(''); setToast('');
      const action = locking ? 'lock' : 'unlock';
      const res = await fetch(`${API_BASE}/api/weeks/${encodeURIComponent(selectedWeekId)}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({})
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Failed to ${action} week`);
      setWeeksList(prev => prev.map(w => (w.id === selectedWeekId ? { ...w, ...data } : w)));
      if (data.lock_time) setLockTimeInput(toLocalLockInput(data.lock_time));
      if (locking) {
        setToast('Week locked');
      } else if (data.lock_adjusted) {
        setToast('Week unlocked; prior/auto lock was past — lock moved to next Sunday noon');
      } else {
        setToast('Week unlocked');
      }
    } catch (e) { setError(e.message); }
  };

  const adminDeletePick = async (pickId) => {
    try {
      setError(''); setToast('');
      const res = await fetch(`${API_BASE}/api/admin/picks/${encodeURIComponent(pickId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok && res.status !== 204) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to delete pick');
      }
      setWeekPicks(prev => prev.filter(p => p.id !== pickId));
      setToast('Pick deleted');
    } catch (e) { setError(e.message); }
  };

  const adminOverridePick = async () => {
    try {
      setError(''); setToast('');
      if (!override.entry_id || !selectedWeekId || !override.team_abbr) throw new Error('Select entry and team');
      const res = await fetch(`${API_BASE}/api/admin/picks/override`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ entry_id: override.entry_id, week_id: selectedWeekId, team_abbr: override.team_abbr })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to override pick');
      // refresh list
      const res2 = await fetch(`${API_BASE}/api/weeks/${encodeURIComponent(selectedWeekId)}/picks`, { headers: { Authorization: `Bearer ${token}` } });
      const data2 = await res2.json().catch(() => []);
      setWeekPicks(Array.isArray(data2) ? data2 : []);
      setToast('Pick overridden');
    } catch (e) { setError(e.message); }
  };

  if (!configBooted) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-6 text-left">
        <div style={{ opacity: 0.8 }}>Loading…</div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 text-left">
      <h1 className="mb-2 text-2xl font-semibold tracking-tight">Admin Weeks & Locks</h1>
      <p className="mb-4 text-sm text-muted-foreground">Connected to: <code>{API_BASE}</code></p>
      <div style={{ marginBottom: 8 }}>
        <Link to="/admin">← Back to Admin Dashboard</Link>
      </div>
      {error && <div style={{ color: 'tomato', marginBottom: 8 }}>{error}</div>}
      {toast && <div role="status" aria-live="polite" style={{ color: 'var(--win-color)', marginBottom: 8 }}>{toast}</div>}

      <CollapsibleSection
        id="active-season"
        title="Active Season"
        subtitle={resolvedActiveSeason != null ? `Active: ${resolvedActiveSeason}${activeSeasonOverride != null ? ' (explicit override)' : ' (derived)'}` : undefined}
        isOpen={activeSeasonOpen}
        onToggle={() => setActiveSeasonOpen(v => !v)}
      >
        <div style={{ display: 'grid', gap: 8 }}>
          <div>
            <strong>Active season:</strong>{' '}
            <span>
              {resolvedActiveSeason == null ? 'none' : resolvedActiveSeason}
              {activeSeasonOverride == null ? ' • derived (no explicit setting)' : ' • explicitly set'}
            </span>
          </div>
          {activeSeasonOverride == null && (
            <div style={{
              padding: 8,
              border: '1px solid var(--loss-border)',
              background: 'var(--loss-bg)',
              fontSize: 13,
            }}>
              No explicit active season. The app is falling back to the current week's season, then the newest
              season with weeks — so new entries are being stamped{' '}
              {resolvedActiveSeason == null ? 'with no season' : resolvedActiveSeason}. Set it explicitly before the
              new season's Week 1 exists.
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <label htmlFor="active-season-input">Set active season
              <input
                id="active-season-input"
                aria-label="Active season"
                value={activeSeasonInput}
                onChange={e => setActiveSeasonInput(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="YYYY"
                style={{ marginLeft: 6, width: 100 }}
              />
            </label>
            {seasonsList.map(s => (
              <button
                key={s.season_year}
                onClick={() => setActiveSeasonInput(String(s.season_year))}
                disabled={savingActiveSeason}
                title={`Use ${s.season_year}`}
              >
                {s.season_year}
              </button>
            ))}
            <button onClick={adminSaveActiveSeason} disabled={!token || savingActiveSeason || !activeSeasonInput}>
              Save Active Season
            </button>
            <button onClick={adminClearActiveSeason} disabled={!token || savingActiveSeason || activeSeasonOverride == null}>
              Clear Override
            </button>
          </div>
          <div style={{ fontSize: 12, opacity: 0.85 }}>
            The active season decides which season users' entries and picks are scoped to, and which season new
            entries are stamped with. You can enter a season that has no weeks yet. Clearing the override reverts to
            the derived value.
          </div>
          {seasonsList.length > 0 && (
            <div style={{ fontSize: 12, opacity: 0.85 }}>
              Known seasons: {seasonsList.map(s => `${s.season_year} (${s.week_count} weeks, ${s.entry_count} entries)`).join(' · ')}
            </div>
          )}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        id="weeks-management"
        title="Weeks Management"
        subtitle={selectedWeek ? `Selected: ${selectedWeek.season_year} • Week ${selectedWeek.week_number}${selectedWeek.is_current ? ' (Current)' : ''}` : undefined}
        isOpen={weeksMgmtOpen}
        onToggle={() => setWeeksMgmtOpen(v => !v)}
      >
        <div>
        <div style={{ marginBottom: 8 }}>
          <button
            onClick={async () => {
              if (!window.confirm('Delete ALL weeks, games, and picks? This is destructive and cannot be undone. Type the phrase in the next prompt to confirm.')) return;
              const phrase = window.prompt('Type exactly: DELETE ALL WEEKS');
              if (phrase !== 'DELETE ALL WEEKS') return;
              try {
                setError(''); setToast('');
                const res = await fetch(`${API_BASE}/api/admin/purge/weeks`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify({ confirm: 'DELETE ALL WEEKS' })
                });
                const data = await res.json().catch(() => ({}));
                if (!res.ok) throw new Error(data.error || 'Purge failed');
                setWeeksList([]);
                setSelectedWeekId('');
                setWeekPicks([]);
                setWeekGames([]);
                setToast(`Purged weeks (weeks: ${data?.counts?.weeks ?? 0}, games: ${data?.counts?.games ?? 0}, picks: ${data?.counts?.picks ?? 0})`);
              } catch (e) { setError(e.message); }
            }}
          >
            Delete ALL weeks
          </button>
        </div>
        {syncNotice.text && (
          <div role="status" aria-live="polite" style={{
            marginBottom: 8,
            padding: 8,
            border: syncNotice.type === 'error' ? '1px solid var(--loss-border)' : '1px solid var(--win-border)',
            background: syncNotice.type === 'error' ? 'var(--loss-bg)' : 'var(--win-bg)',
            whiteSpace: 'pre-wrap'
          }}>
            {syncNotice.text}
          </div>
        )}
        {/* Add Week */}
        <div style={{ marginBottom: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <label>New Season
            <input
              value={newSeasonYear}
              onChange={e => setNewSeasonYear(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="e.g., 2024"
              style={{ marginLeft: 6, width: 100 }}
            />
          </label>
          <label>Week
            <input
              value={newWeekNumber}
              onChange={e => setNewWeekNumber(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="e.g., 1"
              style={{ marginLeft: 6, width: 80 }}
            />
          </label>
          {/* 'Set as current' removed; use the Set Current Week button below */}
          <button onClick={adminCreateWeek} disabled={!token || !newSeasonYear || !newWeekNumber}>Add Week</button>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={selectedWeekId} onChange={e => setSelectedWeekId(e.target.value)}>
            <option value="">Select a week…</option>
            {weeksListSortedByCreatedAt.map(w => (
              <option key={w.id} value={w.id}>
                {w.season_year} • Week {w.week_number}{w.is_current ? ' • current' : ''}
              </option>
            ))}
          </select>
          <button onClick={adminSetCurrentWeek} disabled={!selectedWeekId || !token}>Set Current Week</button>
        </div>
        <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="datetime-local" value={lockTimeInput} onChange={e => setLockTimeInput(e.target.value)} />
          <button onClick={adminUpdateLockTime} disabled={!selectedWeekId || !lockTimeInput || !token}>Update Lock Time</button>
          <button onClick={adminResetLockAuto} disabled={!selectedWeekId || !token}>Reset to Sunday 12:00</button>
          <button onClick={adminToggleWeekLock} disabled={!selectedWeekId || !token}>
            {selectedWeekIsLocked ? 'Unlock Week' : 'Lock Week'}
          </button>
          <button onClick={adminDeleteWeek} disabled={!selectedWeekId || !token || (weeksList.find(w => w.id === selectedWeekId)?.is_current)}>Delete Week</button>
        </div>
        {/* ESPN sync controls */}
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed var(--border)', display: 'grid', gap: 8 }}>
          <div style={{ fontWeight: 700 }}>Game Sync (ESPN)</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <label>Cron mode
              <select
                key={cronSelectEpoch}
                aria-label="Cron mode"
                value={cronMode}
                onChange={(e) => adminSetCronMode(e.target.value)}
                disabled={!token}
                style={{ marginLeft: 6 }}
              >
                <option value="off">Off</option>
                <option value="always">Always (every 5 min)</option>
                <option value="windowed">Windowed (game times only)</option>
              </select>
            </label>
            <span style={{ fontSize: 12, opacity: 0.85 }}>
              {cronMode === 'off' && 'Scheduled ESPN sync is paused for this environment.'}
              {cronMode === 'always' && 'Cron runs Update Scores every 5 minutes.'}
              {cronMode === 'windowed' && 'Cron runs only from 5 min before each kickoff through 3.5 hours after.'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <label>Season
              <input
                aria-label="Season year"
                value={seasonYearInput}
                onChange={e => setSeasonYearInput(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="e.g., 2024"
                style={{ marginLeft: 6, width: 100 }}
              />
            </label>
            <label>Week
              <input
                aria-label="Week number"
                value={weekNumberInput}
                onChange={e => setWeekNumberInput(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="e.g., 1"
                style={{ marginLeft: 6, width: 80 }}
              />
            </label>
            <button onClick={() => adminSyncEspn('update_scores')} disabled={!token}>Update Scores</button>
            <button onClick={() => adminSyncEspn('full_resync')} disabled={!token}>Full Re-sync</button>
            <span style={{ fontSize: 12, opacity: 0.8 }}>Leave fields as-is to use the selected week's values.</span>
          </div>
          <div style={{ fontSize: 12, opacity: 0.85 }}>
            <strong>Update Scores</strong> refreshes status/scores/kickoff for existing matchups without changing game IDs or lock/ineligible flags
            (also what the cron does when enabled). <strong>Full Re-sync</strong> replaces the week slate, rematches lock/ineligible flags by
            matchup, auto-marks Thursday games ineligible, and rebuilds bye teams. Confirm is required when the week already has games.
          </div>
          {/* Randomization controls */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ fontWeight: 600 }}>Randomize (testing only)</div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={overwritePicks} onChange={e => setOverwritePicks(e.target.checked)} /> Overwrite existing picks
            </label>
            <button onClick={adminRandomizePicks} disabled={!token || !selectedWeekId}>Randomize Picks</button>
            <button onClick={adminRandomizeScores} disabled={!token || !selectedWeekId}>Randomize Scores</button>
            <button onClick={adminFinalizeScores} disabled={!token || !selectedWeekId}>Set All to Final</button>
          </div>
        </div>
        <div style={{ marginTop: 8 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Bye teams</div>
          <div style={{ fontSize: 13, opacity: 0.9 }}>
            {!selectedWeekId
              ? 'Select a week to view bye teams.'
              : byeTeamsList.length === 0
                ? 'None (set automatically on Full Re-sync from ESPN).'
                : byeTeamsList.join(', ')}
          </div>
        </div>
        {selectedWeekId && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Games for Selected Week</div>
            <div style={{ overflow: 'auto', border: '1px solid var(--border)' }}>
              {weekGames.length === 0 ? (
                <div style={{ padding: 8, opacity: 0.8 }}>No games</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>Kickoff</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>Away</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>Home</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>Status</th>
                      <th style={{ textAlign: 'right', borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>Score</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weekGames.map(g => {
                      const isLocked = !!g.is_locked;
                      const isIneligible = !!g.is_ineligible;
                      return (
                        <tr key={g.id}>
                          <td style={{ borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>{formatKickoff(g.start_time)}</td>
                          <td style={{ borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>{g.away_team_abbr}</td>
                          <td style={{ borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>{g.home_team_abbr}</td>
                          <td style={{ borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>
                            {(g.status || 'scheduled')}
                            {isLocked ? ' • Locked' : ''}
                            {isIneligible ? ' • Ineligible' : ''}
                          </td>
                          <td style={{ borderBottom: '1px solid var(--border)', padding: '6px 8px', textAlign: 'right' }}>
                            {(typeof g.away_score === 'number' ? g.away_score : 0)} - {(typeof g.home_score === 'number' ? g.home_score : 0)}
                          </td>
                          <td style={{ borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <button onClick={() => adminToggleGameFlag(g, { is_locked: !isLocked })} disabled={!selectedWeekId || !token}>
                                {isLocked ? 'Unlock' : 'Lock'}
                              </button>
                              <button onClick={() => adminToggleGameFlag(g, { is_ineligible: !isIneligible })} disabled={!selectedWeekId || !token}>
                                {isIneligible ? 'Eligible' : 'Ineligible'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        id="pick-management"
        title="Pick Management"
        subtitle={
          selectedWeek
            ? `Picks for Selected Week (Season ${selectedWeek.season_year} Week ${selectedWeek.week_number}) • ${activeEntriesCount} active ${activeEntriesCount === 1 ? 'entry' : 'entries'}`
            : 'Picks for Selected Week'
        }
        isOpen={picksMgmtOpen}
        onToggle={() => setPicksMgmtOpen(v => !v)}
      >
        <div style={{ minWidth: 0, maxWidth: '100%' }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Picks for Selected Week</div>
          <div
            data-testid="pick-management-toolbar"
            style={{ marginBottom: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', minWidth: 0, maxWidth: '100%' }}
          >
            {/* Counter: number of active entries (unique entry_ids among picks + placeholders) */}
            <div style={{ marginRight: 12, minWidth: 0 }}>
              <strong>Active entries:</strong> {activeEntriesCount}
            </div>
            <div
              data-testid="pick-management-controls"
              style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0, maxWidth: '100%' }}
            >
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', minWidth: 0, maxWidth: '100%' }}>
                Filter
                <select
                  aria-label="Filter picks"
                  value={pickFilter}
                  onChange={e => setPickFilter(e.target.value)}
                  disabled={!selectedWeekId}
                  style={{ minWidth: 0, maxWidth: '100%' }}
                >
                  <option value="all">All</option>
                  <option value="noPick">No pick submitted</option>
                  <option value="hasPick">Pick submitted</option>
                </select>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', minWidth: 0, maxWidth: '100%' }}>
                Sort
                <select
                  aria-label="Sort picks"
                  value={pickSort}
                  onChange={e => setPickSort(e.target.value)}
                  disabled={!selectedWeekId}
                  style={{ minWidth: 0, maxWidth: '100%' }}
                >
                  <option value="default">Default</option>
                  <option value="noPickFirst">No-pick first</option>
                  <option value="byEntry">By entry</option>
                  <option value="byUser">By user</option>
                  <option value="byEmail">By email</option>
                </select>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', minWidth: 0, maxWidth: '100%' }}>
                Search
                <input
                  aria-label="Search picks"
                  placeholder="name, email, or phone"
                  value={pickSearch}
                  onChange={e => setPickSearch(e.target.value)}
                  disabled={!selectedWeekId}
                  style={{ marginLeft: 4, minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}
                />
              </label>
            </div>
            <button
              onClick={adminLoadEligibleNoPickRows}
              disabled={!selectedWeekId || loadingEligible}
              style={{ maxWidth: '100%' }}
            >
              {loadingEligible ? 'Loading…' : 'Load eligible entries without picks'}
            </button>
            <button
              onClick={() => {
                try {
                  const w = weeksList.find(x => x.id === selectedWeekId) || {};
                  // Export all table columns plus additional identifiers
                  const header = [
                    'week_id','season_year','week_number',
                    'user_id','user_name','user_email','user_mobile',
                    'entry_id','entry_name',
                    'team_abbr','result'
                  ];
                  const csvEscape = (v) => {
                    const s = v == null ? '' : String(v);
                    if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
                    return s;
                  };
                  const lines = [header.join(',')];
                  for (const p of sortedWeekPicks) {
                    const res = p.no_pick || !p.team_abbr ? '' : (p.status || 'tbd');
                    lines.push([
                      csvEscape(selectedWeekId), csvEscape(w.season_year), csvEscape(w.week_number),
                      csvEscape(p.user_id || ''), csvEscape(p.user_name || ''), csvEscape(p.user_email || ''), csvEscape(p.user_mobile || ''),
                      csvEscape(p.entry_id || ''), csvEscape(p.entry_name || ''),
                      csvEscape(p.team_abbr || ''), csvEscape(res)
                    ].join(','));
                  }
                  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `week_${w.season_year || 'unknown'}_${w.week_number || 'unknown'}_picks.csv`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                } catch (e) {
                  setError((e && e.message) || 'Failed to export picks');
                }
              }}
              disabled={!selectedWeekId}
              style={{ maxWidth: '100%' }}
            >Export picks (CSV)</button>
          </div>
            <div style={{ overflow: 'auto', border: '1px solid var(--border)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>Email</th>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>Mobile</th>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>Entry</th>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>Result</th>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>Team</th>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
          {!selectedWeekId ? (
                    <tr>
            <td colSpan={6} style={{ borderBottom: '1px solid var(--border)', padding: '6px 8px', opacity: 0.8 }}>
                        Select a week to view picks
                      </td>
                    </tr>
                  ) : (
                  sortedWeekPicks.map(p => {
                    const r = p.no_pick || !p.team_abbr ? '—' : (p.status || 'tbd');
                    const hasNoPick = !!(p.no_pick || !p.team_abbr);
                    const reminderText = selectedWeek?.week_number
                      ? `Reminder: Please submit your pick for Week ${selectedWeek.week_number}`
                      : 'Reminder: Please submit your pick';
                    const smsUri = buildSmsUri({
                      phone: p.user_mobile,
                      body: hasNoPick ? reminderText : '',
                    });
                    const formattedMobile = formatPhone(p.user_mobile);
                    const badgeStyle = (res) => ({
                      display: 'inline-block', padding: '2px 6px', borderRadius: 4, border: '1px solid var(--tbd-border)',
                      background: res === 'win' ? 'var(--win-bg)' : res === 'loss' ? 'var(--loss-bg)' : 'var(--tbd-bg)',
                      color: res === 'win' ? 'var(--win-color)' : res === 'loss' ? 'var(--loss-color)' : undefined
                    });
                    return (
                      <tr key={p.id}>
                        <td style={{ borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>{p.user_email || '—'}</td>
                        <td style={{ borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>
                          {formattedMobile && smsUri ? (
                            <a
                              href={smsUri}
                              style={{ color: 'var(--primary, #2563eb)', textDecoration: 'underline' }}
                              title={hasNoPick ? 'Send SMS reminder' : 'Send SMS'}
                            >
                              {formattedMobile}
                            </a>
                          ) : (
                            formattedMobile || '—'
                          )}
                        </td>
                        <td style={{ borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>{p.entry_name || '—'}</td>
                        <td style={{ borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>
                          {r === '—' ? '—' : <span style={badgeStyle(r)}>{r.toUpperCase()}</span>}
                        </td>
                        <td style={{ borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>
                          <select
                            value={p.team_abbr}
                            onChange={async (e) => {
                              try {
                                setError(''); setToast('');
                                const nextAbbr = e.target.value;
                                // Optimistic update
                                setWeekPicks(prev => prev.map(x => x.id === p.id ? { ...x, team_abbr: nextAbbr } : x));
                                const res = await fetch(`${API_BASE}/api/admin/picks/override`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                  body: JSON.stringify({ entry_id: p.entry_id, week_id: selectedWeekId, team_abbr: nextAbbr })
                                });
                                const data = await res.json().catch(() => ({}));
                                if (!res.ok) throw new Error(data.error || 'Failed to change pick');
                                setToast('Pick updated');
                              } catch (e) {
                                setError(e.message);
                                // revert by refetching
                                try {
                                  const res2 = await fetch(`${API_BASE}/api/weeks/${encodeURIComponent(selectedWeekId)}/picks`, { headers: { Authorization: `Bearer ${token}` } });
                                  const data2 = await res2.json().catch(() => []);
                                  setWeekPicks(Array.isArray(data2) ? data2 : []);
                                } catch {}
                              }
                            }}
                            disabled={!selectedWeekId}
                          >
                            <option value="">— select —</option>
                            {teams.map(t => {
                              const selWeek = weeksList.find(w => w.id === selectedWeekId);
                              const ineligible = (selWeek?.ineligible_teams || []).includes(t.abbreviation);
                              const g = weekGames.find(gm => gm && (gm.home_team_abbr === t.abbreviation || gm.away_team_abbr === t.abbreviation));
                              const isLocked = !!(g && g.is_locked);
                              const disabled = ineligible || isLocked;
                              const suffix = disabled ? `${ineligible ? ' • ineligible' : ''}${isLocked ? ' • locked' : ''}` : '';
                              return (
                                <option key={t.abbreviation} value={t.abbreviation} disabled={disabled}>
                                  {t.city ? `${t.city} ` : ''}{t.name} ({t.abbreviation}){suffix}
                                </option>
                              );
                            })}
                          </select>
                        </td>
                        <td style={{ borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>
                          {!p.no_pick && !String(p.id || '').startsWith('nopick-') && (
                            <button onClick={() => adminDeletePick(p.id)}>Clear</button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                  )}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                placeholder="Entry ID"
                value={override.entry_id}
                onChange={e => setOverride(prev => ({ ...prev, entry_id: e.target.value }))}
                disabled={!selectedWeekId}
                style={{ width: 180 }}
              />
              <input
                placeholder="Team abbr (e.g., KC)"
                value={override.team_abbr}
                onChange={e => setOverride(prev => ({ ...prev, team_abbr: e.target.value.toUpperCase() }))}
                disabled={!selectedWeekId}
                style={{ width: 160 }}
              />
              <button onClick={adminOverridePick} disabled={!selectedWeekId}>Override/Add Pick</button>
            </div>
          </div>
        </CollapsibleSection>
    </div>
  );
}

function CollapsibleSection({ id, title, subtitle, isOpen, onToggle, children, headerExtra }) {
  return (
    <Card className="mb-4">
      <CardHeader className={isOpen ? 'pb-3' : 'pb-6'}>
        <div className="flex w-full items-center justify-between gap-2">
          <button
            type="button"
            id={id ? `${id}-toggle` : undefined}
            aria-controls={id ? `${id}-content` : undefined}
            aria-expanded={isOpen}
            className="flex flex-1 cursor-pointer items-center justify-between gap-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
            onClick={onToggle}
          >
            <div>
              <CardTitle>{title}</CardTitle>
              {subtitle && <div className="text-sm text-muted-foreground mt-0.5">{subtitle}</div>}
            </div>
            <ChevronDown
              className={cn(
                'h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200',
                isOpen && 'rotate-180'
              )}
              aria-hidden="true"
            />
          </button>
          {headerExtra && <div className="shrink-0">{headerExtra}</div>}
        </div>
      </CardHeader>
      {isOpen && (
        <CardContent id={id ? `${id}-content` : undefined}>
          {children}
        </CardContent>
      )}
    </Card>
  );
}
